import { COURSE_VERSION } from '../version'

export type ProgressEventKind = 'section_completed' | 'last_position'

export interface ProgressEvent {
  eventId: string
  kind: ProgressEventKind
  courseId: string
  moduleId: string
  sectionId: string
  occurredAt: string
}

export interface CompletedSection {
  courseId: string
  moduleId: string
  sectionId: string
  completedAt: string
}

export interface LastPosition {
  courseId: string
  moduleId: string
  sectionId: string
  updatedAt: string
}

export interface CourseProgress {
  schemaVersion: 1
  courseVersion: string
  revision: number
  completedSections: Record<string, CompletedSection>
  lastPosition: LastPosition | null
  pendingEvents: ProgressEvent[]
  updatedAt: string
}

export interface CloudProgress {
  courseVersion: string
  revision: number
  completedSections: CompletedSection[]
  lastPosition: LastPosition | null
  updatedAt: string
}

const STORAGE_KEY = 'investment-course.progress.v1'

export function sectionKey(moduleId: string, sectionId: string): string {
  return `${moduleId}:${sectionId}`
}

export function newProgress(now = new Date().toISOString()): CourseProgress {
  return {
    schemaVersion: 1,
    courseVersion: COURSE_VERSION,
    revision: 0,
    completedSections: {},
    lastPosition: null,
    pendingEvents: [],
    updatedAt: now,
  }
}

function validPosition(value: unknown): value is LastPosition {
  if (!value || typeof value !== 'object') return false
  const position = value as Partial<LastPosition>
  return ['courseId', 'moduleId', 'sectionId', 'updatedAt'].every((key) => typeof position[key as keyof LastPosition] === 'string')
}

function validCompleted(value: unknown): value is CompletedSection {
  if (!value || typeof value !== 'object') return false
  const item = value as Partial<CompletedSection>
  return ['courseId', 'moduleId', 'sectionId', 'completedAt'].every((key) => typeof item[key as keyof CompletedSection] === 'string')
}

function validEvent(value: unknown): value is ProgressEvent {
  if (!value || typeof value !== 'object') return false
  const event = value as Partial<ProgressEvent>
  return (
    typeof event.eventId === 'string'
    && (event.kind === 'section_completed' || event.kind === 'last_position')
    && ['courseId', 'moduleId', 'sectionId', 'occurredAt'].every((key) => typeof event[key as keyof ProgressEvent] === 'string')
  )
}

export function sanitiseProgress(value: unknown): CourseProgress {
  if (!value || typeof value !== 'object') return newProgress()
  const item = value as Partial<CourseProgress>
  const completedSections: Record<string, CompletedSection> = {}
  if (item.completedSections && typeof item.completedSections === 'object' && !Array.isArray(item.completedSections)) {
    for (const completed of Object.values(item.completedSections)) {
      if (validCompleted(completed)) completedSections[sectionKey(completed.moduleId, completed.sectionId)] = completed
    }
  }
  const seenEvents = new Set<string>()
  const pendingEvents = (Array.isArray(item.pendingEvents) ? item.pendingEvents : [])
    .filter(validEvent)
    .filter((event) => {
      if (seenEvents.has(event.eventId)) return false
      seenEvents.add(event.eventId)
      return true
    })
  return {
    schemaVersion: 1,
    courseVersion: COURSE_VERSION,
    revision: Number.isInteger(item.revision) && Number(item.revision) >= 0 ? Number(item.revision) : 0,
    completedSections,
    lastPosition: validPosition(item.lastPosition) ? item.lastPosition : null,
    pendingEvents,
    updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : new Date().toISOString(),
  }
}

export function loadProgress(): CourseProgress {
  try {
    return sanitiseProgress(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null'))
  } catch {
    return newProgress()
  }
}

export function saveProgress(progress: CourseProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
  } catch {
    // The course stays usable; the UI reports that resume storage is unavailable.
  }
}

function appendEvent(events: ProgressEvent[], event: ProgressEvent): ProgressEvent[] {
  if (event.kind === 'last_position') {
    return [...events.filter((item) => item.kind !== 'last_position'), event]
  }
  if (events.some((item) => item.kind === event.kind && item.moduleId === event.moduleId && item.sectionId === event.sectionId)) {
    return events
  }
  return [...events, event]
}

export function completeSection(
  progress: CourseProgress,
  input: { courseId: string; moduleId: string; sectionId: string },
  now = new Date().toISOString(),
  eventId: string = crypto.randomUUID(),
): CourseProgress {
  const key = sectionKey(input.moduleId, input.sectionId)
  if (progress.completedSections[key]) return progress
  const completed: CompletedSection = { ...input, completedAt: now }
  const event: ProgressEvent = { eventId, kind: 'section_completed', ...input, occurredAt: now }
  return {
    ...progress,
    completedSections: { ...progress.completedSections, [key]: completed },
    pendingEvents: appendEvent(progress.pendingEvents, event),
    updatedAt: now,
  }
}

export function recordPosition(
  progress: CourseProgress,
  input: { courseId: string; moduleId: string; sectionId: string },
  now = new Date().toISOString(),
  eventId: string = crypto.randomUUID(),
): CourseProgress {
  const current = progress.lastPosition
  if (current && current.courseId === input.courseId && current.moduleId === input.moduleId && current.sectionId === input.sectionId) {
    return progress
  }
  const position: LastPosition = { ...input, updatedAt: now }
  const event: ProgressEvent = { eventId, kind: 'last_position', ...input, occurredAt: now }
  return {
    ...progress,
    lastPosition: position,
    pendingEvents: appendEvent(progress.pendingEvents, event),
    updatedAt: now,
  }
}

export function mergeProgress(local: CourseProgress, cloud: CloudProgress): CourseProgress {
  const completedSections = { ...local.completedSections }
  for (const completed of cloud.completedSections.filter(validCompleted)) {
    const key = sectionKey(completed.moduleId, completed.sectionId)
    const existing = completedSections[key]
    if (!existing || Date.parse(completed.completedAt) < Date.parse(existing.completedAt)) {
      completedSections[key] = completed
    }
  }
  const cloudPosition = validPosition(cloud.lastPosition) ? cloud.lastPosition : null
  const lastPosition = !local.lastPosition
    ? cloudPosition
    : !cloudPosition
      ? local.lastPosition
      : Date.parse(cloudPosition.updatedAt) > Date.parse(local.lastPosition.updatedAt)
        ? cloudPosition
        : local.lastPosition
  return {
    ...local,
    revision: Math.max(local.revision, Number.isInteger(cloud.revision) ? cloud.revision : 0),
    completedSections,
    lastPosition,
    updatedAt: [local.updatedAt, cloud.updatedAt].filter(Boolean).sort().at(-1) ?? local.updatedAt,
  }
}

export function acknowledgeEvents(
  progress: CourseProgress,
  acceptedEventIds: string[],
  revision: number,
  cloud?: CloudProgress,
): CourseProgress {
  const accepted = new Set(acceptedEventIds)
  const next = {
    ...progress,
    revision: Math.max(progress.revision, revision),
    pendingEvents: progress.pendingEvents.filter((event) => !accepted.has(event.eventId)),
  }
  return cloud ? mergeProgress(next, cloud) : next
}

export const progressStorageKey = STORAGE_KEY
